import { z } from "zod";
import { idSchema, isoDateTimeSchema, paginatedResponseSchema } from "./shared";

const keyedEntityBaseSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  key: z.string().min(1),
  name: z.string().min(1),
});

export const productSchema = keyedEntityBaseSchema.extend({
  ownerUserId: idSchema.nullable(),
  issueCount: z.number().int().nonnegative().default(0),
});

export const clientSchema = keyedEntityBaseSchema.extend({
  issueCount: z.number().int().nonnegative().default(0),
});

export const projectSchema = keyedEntityBaseSchema.extend({
  clientAccountId: idSchema.nullable(),
  productId: idSchema.nullable(),
  status: z.enum(["active", "paused", "completed"]),
  issueCount: z.number().int().nonnegative().default(0),
});

export const researchTrackSchema = keyedEntityBaseSchema.extend({
  status: z.enum(["exploring", "validating", "decided", "archived"]),
  issueCount: z.number().int().nonnegative().default(0),
});

export const chapterSchema = keyedEntityBaseSchema.extend({
  leadUserId: idSchema.nullable(),
  issueCount: z.number().int().nonnegative().default(0),
});

export const cycleSchema = keyedEntityBaseSchema.extend({
  startsAt: isoDateTimeSchema,
  endsAt: isoDateTimeSchema,
  status: z.enum(["planned", "active", "closed"]),
  issueCount: z.number().int().nonnegative().default(0),
});

export const roadmapMilestoneSchema = keyedEntityBaseSchema.extend({
  targetDate: isoDateTimeSchema.nullable(),
  status: z.enum(["planned", "active", "shipped"]),
  issueCount: z.number().int().nonnegative().default(0),
});

export const contextUserSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  name: z.string().min(1),
  email: z.string().email(),
  role: z.enum(["admin", "member"]),
});

export const issueLabelSchema = z.object({
  id: idSchema,
  workspaceId: idSchema,
  name: z.string().min(1),
  color: z.string().min(1),
});

export const createProductRequestSchema = z.object({
  key: z.string().trim().min(1),
  name: z.string().trim().min(1),
  ownerUserId: idSchema.nullable().optional(),
});

export const updateProductRequestSchema = createProductRequestSchema.partial().extend({
  key: z.string().trim().min(1).optional(),
  name: z.string().trim().min(1).optional(),
});

export const createClientRequestSchema = z.object({
  key: z.string().trim().min(1),
  name: z.string().trim().min(1),
});

export const updateClientRequestSchema = createClientRequestSchema.partial().extend({
  key: z.string().trim().min(1).optional(),
  name: z.string().trim().min(1).optional(),
});

export const createProjectRequestSchema = z.object({
  key: z.string().trim().min(1),
  name: z.string().trim().min(1),
  clientAccountId: idSchema.nullable().optional(),
  productId: idSchema.nullable().optional(),
  status: z.enum(["active", "paused", "completed"]).default("active"),
});

export const updateProjectRequestSchema = createProjectRequestSchema.partial().extend({
  key: z.string().trim().min(1).optional(),
  name: z.string().trim().min(1).optional(),
});

export const createResearchTrackRequestSchema = z.object({
  key: z.string().trim().min(1),
  name: z.string().trim().min(1),
  status: z.enum(["exploring", "validating", "decided", "archived"]).default("exploring"),
});

export const updateResearchTrackRequestSchema = createResearchTrackRequestSchema.partial().extend({
  key: z.string().trim().min(1).optional(),
  name: z.string().trim().min(1).optional(),
});

export const createChapterRequestSchema = z.object({
  key: z.string().trim().min(1),
  name: z.string().trim().min(1),
  leadUserId: idSchema.nullable().optional(),
});

export const updateChapterRequestSchema = createChapterRequestSchema.partial().extend({
  key: z.string().trim().min(1).optional(),
  name: z.string().trim().min(1).optional(),
});

export const createCycleRequestSchema = z.object({
  key: z.string().trim().min(1),
  name: z.string().trim().min(1),
  startsAt: isoDateTimeSchema,
  endsAt: isoDateTimeSchema,
  status: z.enum(["planned", "active", "closed"]).default("planned"),
});

export const updateCycleRequestSchema = createCycleRequestSchema.partial().extend({
  key: z.string().trim().min(1).optional(),
  name: z.string().trim().min(1).optional(),
});

export const createRoadmapMilestoneRequestSchema = z.object({
  key: z.string().trim().min(1),
  name: z.string().trim().min(1),
  targetDate: isoDateTimeSchema.nullable().optional(),
  status: z.enum(["planned", "active", "shipped"]).default("planned"),
});

export const updateRoadmapMilestoneRequestSchema = createRoadmapMilestoneRequestSchema
  .partial()
  .extend({
    key: z.string().trim().min(1).optional(),
    name: z.string().trim().min(1).optional(),
  });

export const productsResponseSchema = paginatedResponseSchema(productSchema);
export const clientsResponseSchema = paginatedResponseSchema(clientSchema);
export const projectsResponseSchema = paginatedResponseSchema(projectSchema);
export const researchTracksResponseSchema = paginatedResponseSchema(researchTrackSchema);
export const chaptersResponseSchema = paginatedResponseSchema(chapterSchema);
export const cyclesResponseSchema = paginatedResponseSchema(cycleSchema);
export const roadmapResponseSchema = paginatedResponseSchema(roadmapMilestoneSchema);
export const contextUsersResponseSchema = paginatedResponseSchema(contextUserSchema);
export const issueLabelsResponseSchema = paginatedResponseSchema(issueLabelSchema);
